import {ChangeEvent, KeyboardEvent, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useLocation} from "react-router-dom";
import {IconButton, TextField, Box} from "@mui/material";

import {Colors, Container, IconSend} from "@bounce/shared/ui";
import {
  Header,
  NotificationBar,
  sessionDataHandler,
  addNotification,
  changeEngagementPhase,
  initSession,
  notifications,
  EngagementPhase,
  MessageBubble,
  ChatMessage,
  setPreEngagementMessages,
} from "..";
import {useAuth} from "@bounce/shared/contexts";
import {useAmplitude, useConsumerUser} from "~/contexts";
import {Pages} from "~/bi-events/enum";
import {EMAIL_REGEX} from "@bounce/shared";
import {v4 as uuidv4} from "uuid";
import {map} from "lodash";

const BOT_MESSAGES = {
  askName: "Hi! What's your name?",
  askEmail: (name: string) => `Thanks, ${name}. What's your email?`,
  askEmailNameKnown: (name: string) => `Hi ${name}! What's your email?`,
  error: "Sorry, something went wrong. Please try again.",
};

type CollectionStep = "name" | "email" | "submitting" | "done";

export const PreEngagementChatPhase = () => {
  const dispatch = useDispatch();
  const {pageClickEvent} = useAmplitude();
  const {token, userId} = useAuth();
  const {userContext} = useConsumerUser();
  const {search, state} = useLocation();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get userId from context or URL parameter
  const queryParams = new URLSearchParams(search);
  const urlUserId = queryParams.get("id") || (state as {userId?: string})?.userId;
  const effectiveUserId = userId || urlUserId;

  // Check if we already have the user's name
  const existingName = userContext?.user_name;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [step, setStep] = useState<CollectionStep>(existingName ? "email" : "name");
  const [collectedData, setCollectedData] = useState({name: existingName || "", email: ""});
  const [isSending, setIsSending] = useState(false);

  // Clear any old pre-engagement messages when this component mounts
  useEffect(() => {
    dispatch(setPreEngagementMessages([]));
  }, [dispatch]);

  // Initialize with appropriate message based on whether we have the name
  useEffect(() => {
    const initialMessages: ChatMessage[] = existingName
      ? [
          {
            id: "ask-email",
            author: "bot",
            body: BOT_MESSAGES.askEmailNameKnown(existingName),
            timestamp: new Date(),
          },
        ]
      : [
          {
            id: "ask-name",
            author: "bot",
            body: BOT_MESSAGES.askName,
            timestamp: new Date(),
          },
        ];
    setMessages(initialMessages);
  }, [existingName]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
  }, [messages]);

  // Focus input
  useEffect(() => {
    textAreaRef.current?.focus();
  }, [step]);

  const addBotMessage = (body: string) => {
    const newMessage: ChatMessage = {
      id: `bot-${uuidv4()}`,
      author: "bot",
      body,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const addUserMessage = (body: string) => {
    const newMessage: ChatMessage = {
      id: `user-${uuidv4()}`,
      author: "user",
      body,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
  };

  const formatMessagesAsQuery = (msgs: ChatMessage[]): string => {
    // Send messages as JSON array to preserve structure
    return JSON.stringify(
      msgs.map((msg) => ({
        author: msg.author,
        body: msg.body,
        timestamp: msg.timestamp.toISOString(),
      }))
    );
  };

  const handleSubmitData = async (name: string, email: string, currentMessages: ChatMessage[]) => {
    setStep("submitting");
    const allMessages = currentMessages;
    setMessages(allMessages);

    // Format all messages as the query to send to the backend
    const query = formatMessagesAsQuery(allMessages);

    pageClickEvent({page: Pages.CHAT, cta: "start_chat", data: {chat_text: query}});
    // Save messages to Redux so they can be displayed during loading
    dispatch(setPreEngagementMessages(allMessages));
    dispatch(changeEngagementPhase({phase: EngagementPhase.Loading}));

    try {
      const data = await sessionDataHandler.fetchAndStoreNewSession({
        formData: {
          friendly_name: name,
          email,
          query,
        },
        userId: effectiveUserId,
        userToken: token,
      });
      // @ts-ignore
      dispatch(initSession({token: data.token, conversationSid: data.conversation_sid}));
      setStep("done");
    } catch (err) {
      console.error("PreEngagementChatPhase", err);
      dispatch(addNotification(notifications.failedToInitSessionNotification((err as Error).message)));
      dispatch(changeEngagementPhase({phase: EngagementPhase.PreEngagementChat}));
      addBotMessage(BOT_MESSAGES.error);
      setStep("name");
      setCollectedData({name: "", email: ""});
    }
  };

  const handleSend = async () => {
    const trimmedInput = inputText.trim();
    if (!trimmedInput || isSending || step === "submitting" || step === "done") {
      return;
    }

    setIsSending(true);
    addUserMessage(trimmedInput);
    setInputText("");

    // Small delay to make it feel more natural
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (step === "name") {
      const name = trimmedInput;
      setCollectedData((prev) => ({...prev, name}));
      addBotMessage(BOT_MESSAGES.askEmail(name));
      setStep("email");
    } else if (step === "email") {
      if (!validateEmail(trimmedInput)) {
        addBotMessage("That doesn't look like a valid email address. Please try again.");
        setIsSending(false);
        return;
      }
      const email = trimmedInput;
      setCollectedData((prev) => ({...prev, email}));
      // Pass current messages including the user's email response
      const userEmailMessage: ChatMessage = {
        id: `user-${uuidv4()}`,
        author: "user",
        body: trimmedInput,
        timestamp: new Date(),
      };
      await handleSubmitData(collectedData.name, email, [...messages, userEmailMessage]);
    }

    setIsSending(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLInputElement).form?.dispatchEvent(new Event("submit", {cancelable: true, bubbles: true}));
    }
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };

  const isLastOfUserGroup = (index: number) => {
    const nextMessage = messages[index + 1];
    if (!nextMessage) return true;
    return nextMessage.author !== messages[index].author;
  };

  const isInputDisabled = step === "submitting" || step === "done" || isSending;

  return (
    <>
      <Header />
      <NotificationBar />

      <Box
        sx={{
          flexGrow: 1,
          justifyContent: "flex-end",
          overflow: "auto",
          display: "flex",
          flexDirection: "column-reverse",
        }}
      >
        <Box sx={{display: "flex", flexDirection: "column-reverse", flex: 1, mt: "auto", overflow: "auto"}}>
          <Box
            aria-label="Chat messages"
            role="log"
            aria-relevant="additions"
            sx={{display: "flex", flexDirection: "column", p: "12px", flex: 1}}
          >
            {map(messages, (message, index) => (
              <MessageBubble key={message.id} message={message} isLastOfUserGroup={isLastOfUserGroup(index)} />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        </Box>
      </Box>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
      >
        <Container
          sx={{
            backgroundColor: Colors.grey._100,
            borderRadius: "8px",
            p: "8px",
            m: "8px",
            opacity: isInputDisabled ? 0.6 : 1,
            gap: "4px",
            alignItems: "stretch",
          }}
        >
          <TextField
            inputRef={textAreaRef}
            data-test="pre-engagement-chat-input"
            placeholder={
              step === "name" ? "Enter your name..." : step === "email" ? "Enter your email..." : "Connecting..."
            }
            value={inputText}
            onChange={onChange}
            disabled={isInputDisabled}
            onKeyDown={onKeyDown}
            multiline
            maxRows={4}
            fullWidth
            variant="standard"
            slotProps={{
              input: {
                disableUnderline: true,
                sx: {
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: Colors.grey._900,
                  "& ::placeholder": {
                    color: Colors.grey._500,
                    opacity: 1,
                  },
                },
              },
            }}
          />
          <Container row sx={{justifyContent: "flex-end", alignItems: "center", gap: 0}}>
            <IconButton
              data-test="pre-engagement-chat-send"
              type="submit"
              disabled={!inputText.trim() || isInputDisabled}
              aria-label="Send message"
              sx={{
                width: 32,
                height: 32,
                backgroundColor: !inputText.trim() || isInputDisabled ? Colors.grey._400 : Colors.primary._800,
                borderRadius: "50%",
                "&:hover": {
                  backgroundColor: !inputText.trim() || isInputDisabled ? Colors.grey._400 : Colors.primary._900,
                },
                "&.Mui-disabled": {
                  backgroundColor: Colors.grey._400,
                },
              }}
            >
              <IconSend style={{color: Colors.bw.white}} />
            </IconButton>
          </Container>
        </Container>
      </form>
    </>
  );
};
